/**
 * Employees list page — Phase-2 W3.T4.
 *
 * Renders the «Сотрудники» table with three status tabs (Active, Pending,
 * Archive), a filter bar (search + dept + role + page-size), a server-
 * paginated DataTable, and a row Action menu.
 *
 * URL contract (D11 / V2-FIX-09):
 *   ?tab=active|pending|archived  ← controlled by useTabParam
 *   ?search=…                     ← debounced 300ms before pushing
 *   ?dept=<id>                    ← number; empty / "all" omits the filter
 *   ?role=<slug>
 *   ?page=<n>                     ← 1-based; default 1
 *   ?limit=<n>                    ← 25 / 50 / 100
 *
 * Initial mount reads the params, the back-button restores them, and
 * shareable URLs land on the same view. Filter changes use `replace: true`
 * so a chain of keystrokes / clicks doesn't pollute the back-stack.
 *
 * RBAC clamps the visible filter set:
 *   - ADMIN: full set; sees every department and every role.
 *   - MANAGER (РО): department filter is locked to own dept (the Select
 *     is rendered disabled with the dept name as the value); role filter
 *     is restricted to EMPLOYEE / LEAD_ENGINEER. The Action menu hides
 *     reset for users at-or-above own peer.
 *   - SUPERVISOR: reaches this page as of supervisor-invites-admin
 *     (2026-06-10). Read access to all rows; the role FILTER shows the
 *     full set (read-only), but invite/reissue/hard-delete are clamped to
 *     ADMIN-tier targets (see employees-columns + visible-roles). Archive
 *     and password-reset stay hidden.
 *   - EMPLOYEE: never reaches this page (RequireAccessLevel redirects to
 *     /forbidden in W3.T5; this component still runs the `useMe()` lookup
 *     defensively and renders a stub if the actor is not allowed, in case
 *     the route guard is bypassed during tests).
 *
 * Mutations (archive, reissue invite, issue reset) call the W3.T1/T3
 * factories. The "Open detail" + "Add employee" actions are stubbed for
 * Wave 4 — the kebab item fires a callback that today only logs.
 */
import * as React from 'react';
import { Plus, Search } from 'lucide-react';

import type { ListUsersQuery, RoleSlug, UserStatus, UserSummary } from '@aris/shared';
import { ROLE_ACCESS_LEVEL } from '@aris/shared';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTable, type SortingState } from '@/components/ui/data-table';

import { useUsersQuery } from '@/api/users';
import { useDepartmentsQuery, type DepartmentRow } from '@/api/departments';
import { useRolesQuery, type RoleRow } from '@/api/roles';
import { useMe } from '@/hooks/use-me';
import { useTabParam } from '@/hooks/use-tab-param';
import { useSearchParams } from 'react-router-dom';

import { useDebouncedValue } from './use-debounced-value';
import { buildEmployeeColumns, type ActorContext } from './employees-columns';
import { EMPLOYEE_CARD_PARAM } from '@/lib/employee-card-link';
import { EmployeesDetailModal } from './modals/employees-detail.modal';
import { EmployeesAddModal } from './modals/employees-add.modal';
import { EmployeesArchiveDialog } from './modals/employees-archive.dialog';
import { EmployeesHardDeleteDialog } from './modals/employees-hard-delete.dialog';
import { EmployeesReissueInviteDialog } from './modals/employees-reissue-invite.dialog';
import { EmployeesResetPasswordDialog } from './modals/employees-reset-password.dialog';
import type { UserDetail } from '@aris/shared';

const TAB_VALUES = ['active', 'pending', 'archived'] as const;
type TabValue = (typeof TAB_VALUES)[number];

const TAB_TO_STATUS: Readonly<Record<TabValue, UserStatus>> = {
  active: 'ACTIVE',
  pending: 'PENDING',
  archived: 'ARCHIVED',
};

const ALL_VALUE = '__all__';
const DEFAULT_PAGE_SIZE = 50;

// ─── Helpers ─────────────────────────────────────────────────────────────

function emptyCopy(tab: TabValue): string {
  if (tab === 'active') return 'Пока нет сотрудников';
  if (tab === 'pending') return 'Нет ожидающих активации';
  return 'Архив пуст';
}

function toMap<T extends { id: number }>(rows: T[] | undefined): ReadonlyMap<number, T> {
  const m = new Map<number, T>();
  for (const r of rows ?? []) m.set(r.id, r);
  return m;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Roles allowed in the role-FILTER Select for the current actor (this is
 * a read-only list filter — DISTINCT from `visibleAssignableRoles` in
 * modals/visible-roles.ts, which clamps the INVITE role select). ADMIN
 * sees the full assignable list; MANAGER only the ranks they can manage
 * (EMPLOYEE / LEAD_ENGINEER per Phase-2 RBAC matrix).
 *
 * supervisor-invites-admin (2026-06-10): SUPERVISOR returns the FULL set
 * here. This is an INTENTIONAL divergence from `visibleAssignableRoles`
 * (which clamps SUPERVISOR to ADMIN-tier only): filtering is read-only —
 * the supervisor should be able to filter the list by ANY role to find
 * users, even roles it cannot assign. Do NOT "unify" these two helpers.
 */
export function visibleRolesFor(actor: ActorContext, all: RoleRow[]): RoleRow[] {
  if (actor.accessLevel === 'ADMIN') return all;
  if (actor.accessLevel === 'SUPERVISOR') return all;
  if (actor.accessLevel === 'MANAGER') {
    // TIER-BASED (FP-1): a hardcoded `EMPLOYEE | LEAD_ENGINEER` allowlist silently
    // drops new EMPLOYEE-tier slugs — «Инженер» (ENGINEER, Block 2) regressed this
    // way. Mirror visibleAssignableRoles: filter by the compile-time tier map.
    return all.filter((r) => ROLE_ACCESS_LEVEL[r.slug] === 'EMPLOYEE');
  }
  return [];
}

// ─── Page component ──────────────────────────────────────────────────────

interface ResolvedFilters {
  search: string;
  departmentId: number | undefined;
  roleSlug: RoleSlug | undefined;
  page: number;
  limit: number;
}

function readFilters(searchParams: URLSearchParams): ResolvedFilters {
  const rawSearch = (searchParams.get('search') ?? '').trim();
  const rawDept = searchParams.get('dept');
  const rawRole = searchParams.get('role');
  const rawPage = Number(searchParams.get('page') ?? '1');
  const rawLimit = Number(searchParams.get('limit') ?? String(DEFAULT_PAGE_SIZE));
  return {
    search: rawSearch,
    departmentId:
      rawDept && rawDept !== ALL_VALUE && Number.isFinite(Number(rawDept))
        ? Number(rawDept)
        : undefined,
    roleSlug: rawRole && rawRole !== ALL_VALUE ? (rawRole as RoleSlug) : undefined,
    page: Number.isFinite(rawPage) && rawPage >= 1 ? Math.floor(rawPage) : 1,
    limit:
      Number.isFinite(rawLimit) && rawLimit >= 1 && rawLimit <= 200
        ? Math.floor(rawLimit)
        : DEFAULT_PAGE_SIZE,
  };
}

export function EmployeesListPage(): React.ReactElement {
  const { data: me, isLoading: meLoading } = useMe();
  const [tab, setTab] = useTabParam(TAB_VALUES as unknown as readonly string[], {
    paramKey: 'tab',
    defaultTab: 'active',
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = readFilters(searchParams);

  // Local state for the search input — debounced into the URL so the
  // user doesn't see a jittery cursor on every keystroke.
  const [searchDraft, setSearchDraft] = React.useState<string>(filters.search);
  const debouncedSearch = useDebouncedValue(searchDraft, 300);

  // Sync the URL when the debounced value diverges. Using `replace`
  // collapses keystroke spam in the back-stack.
  React.useEffect(() => {
    if (debouncedSearch === filters.search) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch.length === 0) next.delete('search');
        else next.set('search', debouncedSearch);
        // Searching changes the result set — reset to page 1.
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
    // We intentionally do not depend on `setSearchParams` (stable from
    // react-router) or `filters.search` (already compared above) to
    // avoid re-running the effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Sorting (server-side). Phase-2 ships with default `fullName ASC`
  // until a column header click rewrites it; we keep the local state
  // here so the DataTable can read/write through the controlled API.
  const [sorting, setSorting] = React.useState<SortingState>([{ id: 'fullName', desc: false }]);

  // Reference catalogs.
  const departmentsQuery = useDepartmentsQuery();
  const rolesQuery = useRolesQuery();
  const departments = React.useMemo(
    () => toMap<DepartmentRow>(departmentsQuery.data),
    [departmentsQuery.data],
  );
  const roles = React.useMemo(() => toMap<RoleRow>(rolesQuery.data), [rolesQuery.data]);

  const tabValue: TabValue = (TAB_VALUES as readonly string[]).includes(tab)
    ? (tab as TabValue)
    : 'active';
  const status = TAB_TO_STATUS[tabValue];

  // Actor context for column factories + filter clamps. Memoised so the
  // column factory's useMemo dep tuple doesn't churn on every render.
  const actor = React.useMemo<ActorContext>(
    () => ({
      id: me?.id ?? null,
      accessLevel: me?.accessLevel ?? 'EMPLOYEE',
      departmentId: me?.departmentId ?? null,
      // Feature 1 «Помощник ГИПа»: carry the slug so the row-action `can*`
      // helpers can mirror the BE `canManageUsers` guardrail (an
      // ASSISTANT_GIP sees no archive/reset/reissue/delete affordances).
      roleSlug: me?.roleSlug as RoleSlug | undefined,
    }),
    [me?.id, me?.accessLevel, me?.departmentId, me?.roleSlug],
  );

  // RBAC: for РО we lock the department filter to own dept. The visible
  // list query MUST carry this dept id even if the user hasn't picked
  // anything, so РО can never trip the `?departmentId=` switch and see
  // another dept's rows.
  const effectiveDepartmentId =
    actor.accessLevel === 'MANAGER' ? (actor.departmentId ?? undefined) : filters.departmentId;

  const listQuery: ListUsersQuery = {
    status,
    search: filters.search.length > 0 ? filters.search : undefined,
    departmentId: effectiveDepartmentId,
    roleSlug: filters.roleSlug,
    page: filters.page,
    limit: filters.limit,
  };

  const usersQuery = useUsersQuery(listQuery, {
    enabled: !meLoading && Boolean(me),
  });

  // ── Modal state (Wave 4) ───────────────────────────────────────────────
  // Each modal owns its open/closed state here so a row can hop straight
  // from the detail modal into archive / reset / reissue without having
  // to re-select the user. We stash the current target row in
  // `activeUser` so the dialogs (which take a UserDetail) can render.
  const [detailUserId, setDetailUserId] = React.useState<string | null>(null);
  const [addOpen, setAddOpen] = React.useState(false);
  const [archiveTarget, setArchiveTarget] = React.useState<UserDetail | null>(null);
  const [hardDeleteTarget, setHardDeleteTarget] = React.useState<UserDetail | null>(null);
  const [reissueTarget, setReissueTarget] = React.useState<UserDetail | null>(null);
  const [resetTarget, setResetTarget] = React.useState<UserDetail | null>(null);

  const onOpen = React.useCallback((row: UserSummary) => {
    setDetailUserId(row.id);
  }, []);

  // Блок 2 · Ф8 · В-Э — карточка по ссылке: `/employees?card=<id>`. Так ведёт
  // сводный список прав (`/supervisor/rights`): права меняются в карточке
  // человека, а карточка — окно этой страницы, своего адреса у неё нет.
  //
  // Параметр ОДНОРАЗОВЫЙ: открыв карточку, страница убирает его из адреса
  // (`replace`), чтобы закрытие окна не оставляло ссылку, заново открывающую
  // его при обновлении. Значение, не похожее на идентификатор, отбрасывается:
  // запрос с ним ответил бы 400, а окно осталось бы висеть в загрузке.
  const cardParam = searchParams.get(EMPLOYEE_CARD_PARAM);
  React.useEffect(() => {
    if (cardParam === null) return;
    if (UUID_RE.test(cardParam)) setDetailUserId(cardParam);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(EMPLOYEE_CARD_PARAM);
        return next;
      },
      { replace: true },
    );
    // `setSearchParams` стабилен (react-router) — как и в эффекте поиска выше.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardParam]);
  const onArchive = React.useCallback((row: UserSummary) => {
    // The list rows already match the UserSummary shape (= UserDetail
    // alias on Phase 2 — they share columns), so we can pass directly.
    setArchiveTarget(row as UserDetail);
  }, []);
  const onHardDelete = React.useCallback((row: UserSummary) => {
    setHardDeleteTarget(row as UserDetail);
  }, []);
  const onReissue = React.useCallback((row: UserSummary) => {
    setReissueTarget(row as UserDetail);
  }, []);
  const onIssueReset = React.useCallback((row: UserSummary) => {
    setResetTarget(row as UserDetail);
  }, []);

  const columns = React.useMemo(
    () =>
      buildEmployeeColumns({
        actor,
        departments,
        roles,
        onOpen,
        onArchive,
        onHardDelete,
        onReissueInvite: onReissue,
        onIssueReset,
      }),
    [actor, departments, roles, onOpen, onArchive, onHardDelete, onReissue, onIssueReset],
  );

  // ── URL writers for filter selects ─────────────────────────────────────
  const setUrlParam = React.useCallback(
    (key: string, value: string | undefined) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!value) next.delete(key);
          else next.set(key, value);
          if (key !== 'page') next.set('page', '1');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handlePageChange = React.useCallback(
    (n: number) => setUrlParam('page', String(n)),
    [setUrlParam],
  );
  const handlePageSizeChange = React.useCallback(
    (n: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('limit', String(n));
          next.set('page', '1');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // ── Render ─────────────────────────────────────────────────────────────

  const visibleRoles = visibleRolesFor(actor, rolesQuery.data ?? []);
  // supervisor-invites-admin (2026-06-10): SUPERVISOR may now reach this
  // page and add ADMIN-tier invites, so it joins MANAGER + ADMIN here.
  const canSeePage =
    actor.accessLevel === 'MANAGER' ||
    actor.accessLevel === 'ADMIN' ||
    actor.accessLevel === 'SUPERVISOR';
  // `canAdd` aliases `canSeePage`: every actor allowed on the page may
  // open the add modal. SUPERVISOR is explicitly included — it can invite
  // ADMIN-tier users (the add modal's role select is clamped to ADMIN-tier
  // by `visibleAssignableRoles`; the BE enforces the tier-EQUALITY rule).
  //
  // Assistant-parity: an ASSISTANT_GIP is ADMIN-tier and now gets ГИП-parity
  // on the invite surface, so the add button shows like any other admin's.
  // The BE bounds every non-supervisor admin's invites below ADMIN tier (they
  // may invite РО/DEPT_HEAD but never appoint an ADMIN-tier account —
  // ONLY_SUPERVISOR_ASSIGNS_ADMIN); the role dropdown (`visibleAssignableRoles`)
  // mirrors that clamp so no dead option renders.
  const canAdd = canSeePage;

  if (meLoading) {
    return (
      <div className="space-y-3" data-testid="employees-page-loading">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!canSeePage) {
    // Defensive fallback — RequireAccessLevel should have redirected.
    return (
      <div data-testid="employees-page-forbidden" className="p-6 text-muted">
        Доступ запрещён
      </div>
    );
  }

  const totalRows = usersQuery.data?.totalRows ?? 0;
  const rows = usersQuery.data?.rows ?? [];

  return (
    <section
      data-testid="employees-page"
      aria-labelledby="employees-page-heading"
      className="space-y-4"
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 id="employees-page-heading" className="text-2xl font-semibold tracking-tight text-ink">
          Сотрудники
        </h1>
        {canAdd ? (
          <Button
            type="button"
            variant="default"
            data-testid="employees-add-btn"
            onClick={() => setAddOpen(true)}
          >
            <Plus className="mr-1.5 size-4" aria-hidden="true" />
            Добавить сотрудника
          </Button>
        ) : null}
      </header>

      <Tabs value={tabValue} onValueChange={setTab}>
        <TabsList aria-label="Фильтр по статусу">
          <TabsTrigger value="active" data-testid="tab-active">
            Активные
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">
            Ожидают активации
          </TabsTrigger>
          <TabsTrigger value="archived" data-testid="tab-archived">
            Архив
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div
        data-testid="employees-filters"
        className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_220px_220px]"
      >
        <label className="relative block">
          <span className="sr-only">Поиск</span>
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted"
          />
          <Input
            data-testid="employees-search"
            type="search"
            placeholder="Поиск по ФИО или email"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            className="pl-8"
          />
        </label>

        <Select
          // РО is locked to own dept — disable the trigger so the
          // visual matches the contract.
          value={
            actor.accessLevel === 'MANAGER'
              ? String(actor.departmentId ?? '')
              : filters.departmentId
                ? String(filters.departmentId)
                : ALL_VALUE
          }
          onValueChange={(v) =>
            actor.accessLevel === 'MANAGER'
              ? undefined
              : setUrlParam('dept', v === ALL_VALUE ? undefined : v)
          }
          disabled={actor.accessLevel === 'MANAGER'}
        >
          <SelectTrigger aria-label="Отдел" data-testid="employees-dept-filter">
            <SelectValue placeholder="Все отделы" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>Все отделы</SelectItem>
            {(departmentsQuery.data ?? []).map((d) => (
              <SelectItem key={d.id} value={String(d.id)}>
                {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.roleSlug ?? ALL_VALUE}
          onValueChange={(v) => setUrlParam('role', v === ALL_VALUE ? undefined : v)}
        >
          <SelectTrigger aria-label="Роль" data-testid="employees-role-filter">
            <SelectValue placeholder="Все роли" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>Все роли</SelectItem>
            {visibleRoles.map((r) => (
              <SelectItem key={r.id} value={r.slug}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable<UserSummary>
        columns={columns}
        data={rows}
        loading={usersQuery.isLoading}
        sorting={sorting}
        onSortingChange={setSorting}
        emptyState={emptyCopy(tabValue)}
        onRowClick={onOpen}
        testIdPrefix="employees-row"
      />

      <Pagination
        page={filters.page}
        pageSize={filters.limit}
        totalRows={totalRows}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <EmployeesDetailModal
        open={detailUserId !== null}
        onOpenChange={(next) => {
          if (!next) setDetailUserId(null);
        }}
        userId={detailUserId}
        actor={{
          id: me?.id ?? '',
          accessLevel: actor.accessLevel,
          departmentId: actor.departmentId,
          // Feature 1 «Помощник ГИПа»: the detail modal's field matrix +
          // footer `can*` gates mirror the BE `canManageUsers` guardrail.
          roleSlug: actor.roleSlug,
          // Ф8 · В-Э: блок «Права» в карточке — по тому же признаку, что страж.
          isAssignable: me?.isAssignable,
        }}
        onReissueInvite={(u) => {
          setDetailUserId(null);
          setReissueTarget(u);
        }}
        onIssueReset={(u) => {
          setDetailUserId(null);
          setResetTarget(u);
        }}
        onArchive={(u) => {
          setDetailUserId(null);
          setArchiveTarget(u);
        }}
        onHardDelete={(u) => {
          setDetailUserId(null);
          setHardDeleteTarget(u);
        }}
      />

      <EmployeesAddModal
        open={addOpen}
        onOpenChange={setAddOpen}
        actor={{
          accessLevel: actor.accessLevel,
          departmentId: actor.departmentId,
          // Feature 1 «Помощник ГИПа» (Wave 5): the inviter's own id +
          // role slug drive the ASSISTANT_GIP auto-bind UX (a GIP inviter
          // auto-binds the assistant to itself; a non-GIP inviter picks).
          id: me?.id,
          roleSlug: me?.roleSlug as RoleSlug | undefined,
        }}
      />

      <EmployeesArchiveDialog
        open={archiveTarget !== null}
        onOpenChange={(next) => {
          if (!next) setArchiveTarget(null);
        }}
        user={archiveTarget}
      />

      <EmployeesHardDeleteDialog
        open={hardDeleteTarget !== null}
        onOpenChange={(next) => {
          if (!next) setHardDeleteTarget(null);
        }}
        user={hardDeleteTarget}
      />

      <EmployeesReissueInviteDialog
        open={reissueTarget !== null}
        onOpenChange={(next) => {
          if (!next) setReissueTarget(null);
        }}
        user={reissueTarget}
      />

      <EmployeesResetPasswordDialog
        open={resetTarget !== null}
        onOpenChange={(next) => {
          if (!next) setResetTarget(null);
        }}
        user={resetTarget}
      />
    </section>
  );
}
